// Preset gallery: Sizes (canvas + boundary formats; keeps the current
// design) and Designs (full documents, thumbnails rendered by the real
// pipeline and cached) plus the recipe randomizer.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useStore } from '../state/store'
import { PRESETS, RECIPES, SIZE_GROUPS, type SizePreset } from '../presets'
import { generate } from '../core/pipeline'
import type { DocumentModel, GenResult } from '../core/types'
import { IconBrand, IconClose, IconDice } from './icons'

const thumbCache = new Map<string, { doc: DocumentModel; result: GenResult }>()

function thumbFor(name: string, build: () => DocumentModel) {
  let hit = thumbCache.get(name)
  if (!hit) {
    const doc = build()
    hit = { doc, result: generate(doc) }
    thumbCache.set(name, hit)
  }
  return hit
}

export function Gallery() {
  const setOverlay = useStore((s) => s.setOverlay)
  const loadDocument = useStore((s) => s.loadDocument)
  const commit = useStore((s) => s.commit)
  const requestFit = useStore((s) => s.requestFit)
  const pushToast = useStore((s) => s.pushToast)
  const rememberSeed = useStore((s) => s.rememberSeed)
  const currentSeed = useStore((s) => s.doc.layout.seed)
  const [tab, setTab] = useState<'sizes' | 'designs'>('sizes')

  const applyDesign = (name: string, doc: DocumentModel) => {
    loadDocument(structuredClone(doc), `Apply preset: ${name}`)
    setOverlay(null)
    pushToast(`${name} applied. Undo restores your design.`, 'ok')
  }

  const applySize = (p: SizePreset) => {
    commit(`Apply size: ${p.name}`, (d) => {
      d.canvas.width = p.canvas.width
      d.canvas.height = p.canvas.height
      d.boundary.kind = p.boundary.kind
      d.boundary.width = p.boundary.width ?? p.canvas.width
      d.boundary.height = p.boundary.height ?? p.canvas.height
      d.boundary.radius = p.boundary.radius ?? Math.min(p.canvas.width, p.canvas.height) / 2
      d.boundary.cornerRadius = p.boundary.cornerRadius ?? 0
    })
    requestFit()
    setOverlay(null)
    pushToast(`${p.name}: ${p.canvas.width} x ${p.canvas.height} mm. Your pattern is kept.`, 'ok')
  }

  const rollRecipe = (name: string, roll: (seed: number) => DocumentModel) => {
    rememberSeed(currentSeed)
    const seed = Math.floor(Math.random() * 1e9) + 1
    loadDocument(roll(seed), `Roll recipe: ${name}`)
    setOverlay(null)
    pushToast(`${name} rolled with seed ${seed}. Roll again for variations.`, 'ok')
  }

  return (
    <div className="dialog gallery holo-frame" role="dialog" aria-modal="true" aria-label="Presets">
      <div className="dialog-head">
        <IconBrand size={16} />
        <h2>Presets</h2>
        <div className="tabs gallery-tabs" role="tablist" aria-label="Preset type">
          <button
            role="tab"
            aria-selected={tab === 'sizes'}
            className={tab === 'sizes' ? 'tab active' : 'tab'}
            onClick={() => setTab('sizes')}
          >
            Sizes
          </button>
          <button
            role="tab"
            aria-selected={tab === 'designs'}
            className={tab === 'designs' ? 'tab active' : 'tab'}
            onClick={() => setTab('designs')}
          >
            Designs
          </button>
        </div>
        <button className="quiet icon-btn" onClick={() => setOverlay(null)} aria-label="Close (Esc)">
          <IconClose size={13} />
        </button>
      </div>
      <div className="dialog-body">
        {tab === 'sizes' && (
          <>
            <p className="hint" style={{ marginTop: 0 }}>
              Sets the canvas and boundary to a physical format; your pattern, channels, and
              controllers stay as they are.
            </p>
            {SIZE_GROUPS.map((g) => (
              <div key={g.group}>
                <div className="palette-section" style={{ paddingLeft: 0 }}>
                  {g.group}
                </div>
                <div className="size-grid">
                  {g.items.map((p) => (
                    <SizeCard key={p.name} preset={p} onApply={applySize} />
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
        {tab === 'designs' && (
          <>
            <div className="gallery-grid">
              {PRESETS.map((p) => (
                <GalleryCard key={p.name} name={p.name} hint={p.hint} onApply={applyDesign} build={p.build} />
              ))}
            </div>
            <div className="palette-section" style={{ paddingLeft: 0, marginTop: 14 }}>
              Recipes: seeded one-roll styles
            </div>
            <div className="recipe-row">
              {RECIPES.map((r) => (
                <button key={r.name} className="icon-btn" onClick={() => rollRecipe(r.name, r.roll)} title={r.hint}>
                  <IconDice size={14} />
                  {r.name}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// Outline sketch of the physical format, drawn to aspect.
function SizeCard({ preset: p, onApply }: { preset: SizePreset; onApply: (p: SizePreset) => void }) {
  const W = 92
  const H = 56
  const s = Math.min(W / p.canvas.width, H / p.canvas.height)
  const w = p.canvas.width * s
  const h = p.canvas.height * s
  const x = (W - w) / 2
  const y = (H - h) / 2
  return (
    <button className="size-card" onClick={() => onApply(p)} title={p.hint}>
      <svg width={W} height={H} aria-hidden="true">
        <rect x={x} y={y} width={w} height={h} fill="none" stroke="var(--line-2)" strokeDasharray="3 2" />
        {p.boundary.kind === 'circle' ? (
          <circle
            cx={W / 2}
            cy={H / 2}
            r={(p.boundary.radius ?? 1) * s}
            fill="var(--accent-soft)"
            stroke="var(--accent-line)"
          />
        ) : (
          <rect
            x={x + ((p.canvas.width - (p.boundary.width ?? p.canvas.width)) / 2) * s}
            y={y + ((p.canvas.height - (p.boundary.height ?? p.canvas.height)) / 2) * s}
            width={(p.boundary.width ?? p.canvas.width) * s}
            height={(p.boundary.height ?? p.canvas.height) * s}
            rx={(p.boundary.cornerRadius ?? 0) * s}
            fill="var(--accent-soft)"
            stroke="var(--accent-line)"
          />
        )}
      </svg>
      <span className="gallery-name">{p.name}</span>
      <span className="size-dims mono">
        {p.canvas.width} x {p.canvas.height} mm
      </span>
    </button>
  )
}

function GalleryCard({
  name,
  hint,
  build,
  onApply,
}: {
  name: string
  hint: string
  build: () => DocumentModel
  onApply: (name: string, doc: DocumentModel) => void
}) {
  const ref = useRef<HTMLCanvasElement>(null)
  const theme = useStore((s) => s.theme)
  const thumb = useMemo(() => thumbFor(name, build), [name, build])

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const W = 168
    const H = 112
    const dpr = window.devicePixelRatio || 1
    canvas.width = W * dpr
    canvas.height = H * dpr
    canvas.style.width = `${W}px`
    canvas.style.height = `${H}px`
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const css = getComputedStyle(document.documentElement)
    const tok = (n: string, fb: string) => css.getPropertyValue(n).trim() || fb
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.fillStyle = tok('--cv-sheet', '#0d1219')
    ctx.fillRect(0, 0, W, H)
    const { doc, result } = thumb
    const s = Math.min(W / doc.canvas.width, H / doc.canvas.height) * 0.94
    ctx.save()
    ctx.translate((W - doc.canvas.width * s) / 2, (H - doc.canvas.height * s) / 2)
    ctx.scale(s, s)
    const path = new Path2D()
    const add = (ring: [number, number][]) => {
      path.moveTo(ring[0][0], ring[0][1])
      for (let i = 1; i < ring.length; i++) path.lineTo(ring[i][0], ring[i][1])
      path.closePath()
    }
    add(result.boundary)
    for (const hole of result.holes) {
      if (hole.circle) {
        path.moveTo(hole.circle.cx + hole.circle.r, hole.circle.cy)
        path.arc(hole.circle.cx, hole.circle.cy, hole.circle.r, 0, Math.PI * 2)
      } else {
        for (const ring of hole.poly) add(ring)
      }
    }
    ctx.fillStyle = tok('--cv-panel', '#2c3644')
    ctx.fill(path, 'evenodd')
    ctx.restore()
  }, [thumb, theme])

  return (
    <button className="gallery-card" onClick={() => onApply(name, thumb.doc)} title={hint}>
      <canvas ref={ref} aria-hidden="true" />
      <span className="gallery-name">{name}</span>
      <span className="gallery-hint">{hint}</span>
    </button>
  )
}
